/**
 * Confidence Ledger row construction (P2).
 *
 * Trust rule: never invent delta values client-side (no 0 / -5 placeholders).
 * Prefer backend-populated candidate.ledger. If a row is synthetic, it must be
 * structurally flagged (synthetic: true) and never look identical to a real row.
 */

/**
 * @typedef {Object} LedgerRow
 * @property {string} check
 * @property {number|null} delta
 * @property {string} [reason]
 * @property {string} [kind]
 * @property {string} [weight_category]
 * @property {boolean} synthetic
 * @property {string} [source]
 */

/**
 * Normalize a backend ledger entry. Does not invent deltas.
 * @param {object} entry
 * @returns {LedgerRow|null}
 */
function normalizeBackendRow(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const hasDelta = entry.delta !== undefined && entry.delta !== null && entry.delta !== '';
  const delta = hasDelta ? Number(entry.delta) : null;
  if (hasDelta && Number.isNaN(delta)) return null;

  const kind = String(
    entry.kind
    || (delta !== null && delta > 0 ? 'bonus' : delta !== null && delta < 0 ? 'penalty' : 'neutral')
  ).toLowerCase();

  return {
    check: entry.check || entry.rule_name || entry.description || 'Ledger',
    delta,
    reason: entry.reason || entry.detail || '',
    kind,
    weight_category: entry.weight_category || entry.category || 'STRUCTURAL',
    // Backend rows are real unless explicitly marked synthetic.
    synthetic: Boolean(entry.synthetic),
    source: entry.source || 'backend',
  };
}

/**
 * Map rule_checks that already carry backend deltas — never invent missing ones.
 * @param {Array<object>} ruleChecks
 * @returns {LedgerRow[]}
 */
function rowsFromRuleCheckDeltas(ruleChecks) {
  if (!Array.isArray(ruleChecks) || ruleChecks.length === 0) return [];

  const rows = [];
  for (const rule of ruleChecks) {
    if (!rule || typeof rule !== 'object') continue;
    if (rule.delta === undefined || rule.delta === null || rule.delta === '') {
      // Missing delta: skip rather than invent 0 / -5.
      continue;
    }
    const delta = Number(rule.delta);
    if (Number.isNaN(delta)) continue;

    const passed = Boolean(rule.passed);
    let kind = 'neutral';
    if (delta > 0) kind = 'bonus';
    else if (delta < 0) kind = 'penalty';
    else if (!passed) kind = 'penalty';

    rows.push({
      check: rule.rule_name || rule.description || 'Rule',
      delta,
      reason: rule.detail || '',
      kind,
      weight_category: rule.category || 'STRUCTURAL',
      synthetic: false,
      source: 'rule_checks_delta',
    });
  }
  return rows;
}

/**
 * Build display rows for the Confidence Ledger.
 *
 * Option (a): use backend ledger only. Never fabricate deltas from pass/fail.
 * If ledger is empty and rule_checks lack deltas, returns [] (UI shows empty state).
 *
 * @param {object|null|undefined} candidate
 * @returns {LedgerRow[]}
 */
export function buildLedgerRows(candidate) {
  if (!candidate || typeof candidate !== 'object') return [];

  const ledger = candidate.ledger;
  if (Array.isArray(ledger) && ledger.length > 0) {
    return ledger.map(normalizeBackendRow).filter(Boolean);
  }

  // Secondary: only backend-authored deltas on rule_checks (no invented values).
  const fromRules = rowsFromRuleCheckDeltas(candidate.rule_checks || []);
  if (fromRules.length > 0) return fromRules;

  return [];
}

/**
 * True if any row is synthetic / approximate.
 * @param {LedgerRow[]} rows
 */
export function hasSyntheticLedgerRows(rows) {
  return (rows || []).some((row) => row && row.synthetic === true);
}

/**
 * Legacy client fallback signature (the bug we removed).
 * Exported only for tests that prove we never emit these invented values.
 */
export const FORBIDDEN_CLIENT_FALLBACK_DELTAS = Object.freeze({
  pass: 0,
  fail: -5,
});
